import { has, isArray, isEmpty, isPlainObject, isString, isNil } from 'lodash-es'
import { default as renderTemplate } from './render_template'
import { analyzeValue, Comparisons } from './utilities'
import transpile from './condition_transpiler'

function analyzeHash(hash, request) {
  return Object.entries(hash).every(([key, value]) => analyzeHashItem(key, value, request))
}

function analyzeHashItem(key, value, request) {
  // AND
  switch (key) {
    case '_and':
      return Array.isArray(value) ? value.every((item) => evaluateBoolean(item, request)) : false
    case '_or':
      return Array.isArray(value) ? value.some((item) => evaluateBoolean(item, request)) : false
    case '_not':
      if (isPlainObject(value)) return isEmpty(value) ? false : !evaluateBoolean(value, request)
      if (Array.isArray(value)) return value.some((item) => !evaluateBoolean(item, request))
      if (typeof value === 'string') return !evaluateBoolean(value, request)

      return false
    default:
      return analyzeSingle(key, value, request)
  }
}

// Compares two arrays where order is ignored but duplicates are significant.
// e.g. tallyEqual([1,2,2], [2,1,2]) → true
//      tallyEqual([1,2,2], [1,2])   → false
function tallyEqual(a, b) {
  if (a.length !== b.length) return false

  const tally = new Map()

  for (const v of a) tally.set(v, (tally.get(v) ?? 0) + 1)
  for (const v of b) tally.set(v, (tally.get(v) ?? 0) - 1)

  return [...tally.values()].every((v) => v === 0)
}

const isBlank = (val) => !val || String(val).match(/^\s*$/)
const exists = (val) => !isBlank(val)

function isTruthy(value) {
  if (isArray(value)) {
    return !isEmpty(value)
  }

  if (isPlainObject(value)) {
    if (isEmpty(value)) {
      return false
    } else if (has(value, ['file', 'metadata'])) {
      return exists(value.file?.name) && exists(value.metadata?.urls?.original)
    } else {
      return true
    }
  }

  return exists(value)
}

function negatedPrefix(val) {
  return typeof val === 'string' && val[0] === '^'
}

function stripPrefix(val) {
  return negatedPrefix(val) ? val.slice(1) : val
}

// Resolves a single operand to its runtime value. When the operand is an
// Array each element is resolved individually.
function resolveOperand(val, request, depth = 0) {
  return Array.isArray(val) ? val.map((item) => analyzeValue(item, request)).flat(depth) : analyzeValue(val, request)
}

function analyzeSingle(key, values, request) {
  try {
    if (!Array.isArray(values) || values.length !== 2) return false

    const [left, right] = values
    let leftValue, rightValue

    switch (key) {
      case '_lt':
        return Comparisons.lt(analyzeValue(left, request), analyzeValue(right, request))
      case '_lte':
        return Comparisons.lte(analyzeValue(left, request), analyzeValue(right, request))
      case '_gt':
        return Comparisons.gt(analyzeValue(left, request), analyzeValue(right, request))
      case '_gte':
        return Comparisons.gte(analyzeValue(left, request), analyzeValue(right, request))
      case '_between':
        if (!Array.isArray(right) || right.length !== 2) return false
        return (
          Comparisons.gte(analyzeValue(left, request), analyzeValue(right[0], request)) &&
          Comparisons.lte(analyzeValue(left, request), analyzeValue(right[1], request))
        )
      case '_in':
        leftValue = resolveOperand(left, request, 1)
        rightValue = resolveOperand(right, request, 1)

        if (!Array.isArray(rightValue)) return false
        if (Array.isArray(leftValue)) {
          if (leftValue.length === 0) return false
          return leftValue.every((l) => rightValue.some((r) => Comparisons.eq(l, r)))
        }

        return rightValue.some((item) => Comparisons.eq(leftValue, item))
      case '_eq':
        // Handle ^ prefix (negation) — only applies to string operands
        if (negatedPrefix(left) || negatedPrefix(right)) {
          const newLeft = stripPrefix(left)
          const newRight = stripPrefix(right)
          const negate = (negatedPrefix(left) ? 1 : 0) + (negatedPrefix(right) ? 1 : 0)

          return negate % 2 === 0
            ? analyzeSingle(key, [newLeft, newRight], request)
            : !analyzeSingle(key, [newLeft, newRight], request)
        }

        if ([left, right].includes('*')) {
          if (right === '*' && left === '*') {
            return true
          }
          if (right === '*') {
            return !isNil(analyzeValue(left, request))
          } else {
            return !isNil(analyzeValue(right, request))
          }
        }

        leftValue = resolveOperand(left, request)
        rightValue = resolveOperand(right, request)

        if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
          return tallyEqual(leftValue, rightValue)
        }

        if (Array.isArray(leftValue) || Array.isArray(rightValue)) {
          return false
        }

        return Comparisons.eq(analyzeValue(left, request), analyzeValue(right, request))
    }
  } catch (e) {
    console.log(e)
    return false
  }
}

function check(conditional, request) {
  if (isPlainObject(conditional)) return analyzeHash(conditional, request)

  return isTruthy(analyzeValue(conditional, request))
}

function evaluateBoolean(conditional, request) {
  if (isString(conditional) || (isPlainObject(conditional) && has(conditional, 'func'))) {
    const value = analyzeValue(conditional, request)
    return isTruthy(value)
  }

  return check(transpile(conditional), request)
}

export default function (conditional, pathMap, relativeTo = null) {
  return evaluateBoolean(conditional, { pathMap, relativeTo })
}
